/*
 * /inflation-calculator — page logic. Math: core.js. Plumbing: ui.js.
 *
 * Four questions people actually bring (Wordstat 01.10.2026, docs/why/inflation.md):
 *   money     — what a sum from one month is worth in another (the head query);
 *   income    — did my salary / rent / price keep up;
 *   own       — my inflation: a basket with my weights, or my own yearly rates;
 *   forecast  — what a sum will need to be in N years.
 * Every answer carries «how it was computed»: which months were multiplied,
 * which years came from official prints, where a currency reform changed the
 * unit — a number that cannot be checked is not an answer.
 */
import * as C from './core.js';
import * as U from './ui.js';

const SLUG = 'inflation-calculator';
const { $, $$, tr, esc } = U;

const S = {
    iso: null, why: 'locale',
    mode: 'money',
    group: 'all',
    convention: null,         // null = the country's default
    official: true,
    region: '',
    money: { amount: 1000, from: null, to: null, dep: NaN },
    income: { a1: 50000, k1: null, a2: 70000, k2: null },
    own: { kind: 'basket', w: null, from: null, to: null, amount: 1000, rates: {} },
    forecast: { amount: 100000, years: 10, how: 'target', rate: 5 },
    cmp: { iso: null, from: null, to: null },
};
let ds = null;          // prepared dataset of the current country (or region)
let nationalDs = null;  // the country itself, for region fallbacks
let lastText = '';      // the copyable summary of the last result
let lastCsv = '';
const T = U.tracker();
let dsB = null;         // the second country of «compare»
let pickerB = null;
let rankOpen = false;   // the all-countries table was asked for
let fxS = null;         // dollar rate of the current country (fx-xx)
let goldS = null;       // gold, USD per troy ounce
const fxgFailed = new Set(); // ids that would not load this visit
let fxgPending = null;       // the fx id whose load is in flight
let prices = null;          // ru-prices: average consumer prices of everyday goods
let pricesState = '';       // '' | 'loading' | 'failed'

// ── boot ──────────────────────────────────────────────────────────────────
async function boot() {
    const h = U.readHash();
    const init = U.initialCountry(SLUG, h.c);
    S.why = init.why;
    readHashInto(h);
    const picker = U.countryPicker({
        root: $('[data-country]'),
        onPick: (iso, meta) => {
            const from = S.iso;
            U.rememberCountry(SLUG, iso);
            S.why = 'picked';
            S.region = '';
            T.touch('country');
            switchCountry(iso, true);
            U.funnel('country_pick', { iso, from, q: meta && meta.searched ? 1 : 0 });
        },
    });
    window.__inflPicker = picker;
    pickerB = U.countryPicker({
        root: $('[data-country-b]'),
        onPick: async (iso, meta) => {
            S.cmp.iso = iso;
            T.touch('cmp_country');
            pickerB.show(iso);
            await ensureB();
            render();
            U.funnel('cmp_pick', { a: S.iso, b: iso, q: meta && meta.searched ? 1 : 0 });
        },
    });
    wire();
    // A shared link reopens a calculation: worth knowing how often that happens.
    if (h.c || h.a || h.f || h.m) U.funnel('from_link', { m: S.mode, c: init.iso });
    await switchCountry(init.iso, false);
    if (init.why === 'locale') {
        U.onGeo(geoIso => {
            if (S.why !== 'locale' || geoIso === S.iso) return;
            S.why = 'geo';
            switchCountry(geoIso, false);
            U.funnel('country_geo', { iso: geoIso });
        });
    }
    U.stage('ready');
}

function readHashInto(h) {
    if (h.m && ['money', 'income', 'own', 'forecast', 'compare'].includes(h.m)) S.mode = h.m;
    if (h.c2 && U.country(h.c2)) S.cmp.iso = h.c2;
    if (h.g) S.group = h.g;
    if (h.cv === 'period' || h.cv === 'point') S.convention = h.cv;
    if (h.of === '0') S.official = false;
    if (h.r) S.region = h.r;
    const amt = U.parseAmount(h.a);
    if (Number.isFinite(amt) && amt > 0) S.money.amount = S.own.amount = amt;
    const f = C.parseYm(h.f), t = C.parseYm(h.t);
    if (Number.isFinite(f)) S.money.from = S.own.from = S.income.k1 = f;
    if (Number.isFinite(t)) S.money.to = S.own.to = S.income.k2 = t;
    if (h.m === 'compare') { if (Number.isFinite(f)) S.cmp.from = f; if (Number.isFinite(t)) S.cmp.to = t; }
    const a2 = U.parseAmount(h.a2);
    if (Number.isFinite(a2) && a2 > 0) S.income.a2 = a2;
    if (h.y && +h.y > 0) S.forecast.years = Math.min(100, +h.y);
    if (h.fh && ['target', 'trailing', 'own'].includes(h.fh)) S.forecast.how = h.fh;
    if (h.fr && Number.isFinite(+h.fr)) S.forecast.rate = +h.fr;
    if (h.dr && Number.isFinite(+h.dr)) S.money.dep = +h.dr;
    if (h.fg === '1') S.fxg = true;
    if (h.pr === '1') S.prices = true;
}

async function switchCountry(iso, user) {
    const c = U.country(iso) || U.country(U.CFG.country);
    S.iso = c.iso;
    window.__inflPicker.show(c.iso, S.why);
    $('#ic-status').textContent = tr('loading');
    try {
        nationalDs = await U.dataset(c.iso);
    } catch (e) {
        $('#ic-status').textContent = tr('load_failed');
        U.diagnostic('dataset_failed', { iso: c.iso, msg: String(e && e.message || e).slice(0, 120) });
        window.__handleError && window.__handleError(e, 'inflation:dataset');
        return;
    }
    if (!nationalDs.groups[S.group]) S.group = 'all';
    await applyRegion();
    setupControls(user);
    render();
}

/**
 * A Russian subject: its own month indices since 2022, its official
 * December-to-December prints since 1992, and — inside a year before 2022,
 * where Rosstat publishes no monthly series by subject — the national months.
 * The breakdown says which is which.
 */
async function applyRegion() {
    ds = nationalDs;
    const box = $('#ic-region-wrap');
    const c = U.country(S.iso);
    box.hidden = !(c && c.regions) || S.group !== 'all';
    if (box.hidden || !S.region) { S.region = box.hidden ? '' : S.region; return; }
    let raw;
    try { raw = await U.regions(S.iso); } catch (e) { S.region = ''; return; }
    const r = raw && raw.regions.find(x => x.code === S.region);
    if (!r) { S.region = ''; return; }
    const nat = nationalDs;
    const rk0 = C.parseYm(raw.first), rk1 = C.parseYm(raw.last);
    const k1 = Math.min(nat.k1, rk1);
    const vals = [];
    for (let k = nat.k0; k <= k1; k++) vals.push(k >= rk0 ? r.series.all[k - rk0] : C.mmAt(nat, 'all', k));
    const annual = Object.assign({}, nat.groups.all.annual ? { 1991: nat.groups.all.annual[1991] } : {}, r.annual);
    ds = C.prepare({ id: 'region', country: S.iso, kind: 'mm', first: C.ymStr(nat.k0), last: C.ymStr(k1), series: { all: vals }, annual: { all: annual } }, nat.meta);
    ds.meta = Object.assign({}, nat.meta, { regionName: r.name, regionFrom: rk0 });
    ds.fetchedAt = raw.fetched_at;
}

// ── controls ──────────────────────────────────────────────────────────────
function setupControls(user) {
    const d = nationalDs;
    const firstK = d.kind === 'level' ? d.k0 : C.firstMonthIndex(d);
    const y0 = C.ky(firstK), y1 = C.ky(d.k1) + (S.mode === 'forecast' ? 0 : 0);
    // Default period: ten years back to the last published month.
    const defFrom = Math.max(firstK, C.mk(C.ky(d.k1) - 10, C.km(d.k1)));
    // The second country of «compare»: never the first one; the US unless the
    // first IS the US. Its own data may end earlier — `to` is clamped below.
    if (!S.cmp.iso || S.cmp.iso === S.iso || !U.country(S.cmp.iso)) S.cmp.iso = S.iso === 'US' ? 'GB' : 'US';
    pickerB.show(S.cmp.iso);
    for (const [a, b] of [['money', 'from'], ['money', 'to'], ['own', 'from'], ['own', 'to'], ['cmp', 'from'], ['cmp', 'to']]) {
        const v = S[a][b];
        if (!Number.isFinite(v) || v < firstK || v > d.k1 || user) S[a][b] = b === 'from' ? defFrom : d.k1;
    }
    if (!Number.isFinite(S.income.k1) || S.income.k1 < firstK || S.income.k1 > d.k1 || user) S.income.k1 = C.mk(C.ky(d.k1) - 5, C.km(d.k1));
    if (!Number.isFinite(S.income.k2) || S.income.k2 < firstK || S.income.k2 > d.k1 || user) S.income.k2 = d.k1;
    $$('[data-my]').forEach(box => {
        const [selM, selY] = [$('select[data-m]', box), $('select[data-y]', box)];
        U.fillMonthYear(selM, selY, y0, y1);
        const [obj, key] = box.dataset.my.split('.');
        U.writeMonth(selM, selY, S[obj][key]);
    });
    // currency label next to sums
    $$('[data-cur]').forEach(el => { el.textContent = currencySymbol(d.currency); });
    // index groups available for this country
    const gSel = $('#ic-group');
    gSel.textContent = '';
    for (const g of ['all', 'food', 'nonfood', 'services']) {
        if (d.groups[g]) gSel.add(new Option(tr('group_' + g), g));
    }
    gSel.value = S.group;
    $('#ic-group-wrap').hidden = gSel.options.length < 2;
    // convention
    $$('[data-cv]').forEach(b => b.classList.toggle('on', b.dataset.cv === (S.convention || d.convention)));
    // official prints only exist for month-index (Rosstat) data
    $('#ic-official-wrap').hidden = d.kind !== 'mm';
    $('#ic-official').checked = S.official;
    // regions
    fillRegions();
    // basket weights default to the official basket
    const hasBasket = ['food', 'nonfood', 'services'].every(g => d.groups[g]);
    $('#ic-own-basket-b').hidden = !hasBasket;
    if (!hasBasket && S.own.kind === 'basket') S.own.kind = 'rates';
    if (!S.own.w || user) S.own.w = d.weights ? { food: d.weights.food, nonfood: d.weights.nonfood, services: d.weights.services } : { food: 40, nonfood: 33, services: 27 };
    $$('[data-w]').forEach(el => { el.value = U.num(S.own.w[el.dataset.w], 1); });
    // forecast labels with this country's numbers
    const t12 = C.trailing12(d, 'all');
    $('[data-fh=target]').textContent = d.target != null ? tr('fc_target', { rate: U.pct(d.target, 1) }) : tr('fc_target_none');
    $('[data-fh=target]').disabled = d.target == null;
    if (d.target == null && S.forecast.how === 'target') S.forecast.how = 'trailing';
    $('[data-fh=trailing]').textContent = tr('fc_trailing', { rate: U.pct(t12, 1) });
    // amounts
    $('#ic-amount').value = U.num(S.money.amount, S.money.amount % 1 ? 2 : 0);
    $('#ic-a1').value = U.num(S.income.a1, 0);
    $('#ic-a2').value = U.num(S.income.a2, 0);
    $('#ic-own-amount').value = U.num(S.own.amount, S.own.amount % 1 ? 2 : 0);
    $('#ic-fc-amount').value = U.num(S.forecast.amount, 0);
    $('#ic-fc-years').value = S.forecast.years;
    $('#ic-fc-rate').value = U.num(S.forecast.rate, 1);
    if (Number.isFinite(S.money.dep)) {
        $('#ic-dep-rate').value = U.num(S.money.dep, S.money.dep % 1 ? 2 : 0);
        $('#ic-dep').open = true;
    }
    if (S.fxg) $('#ic-fxg').open = true;
    if (S.prices && $('#ic-prices')) $('#ic-prices').open = true;
    showMode();
    renderReference();
}

async function fillRegions() {
    const sel = $('#ic-region');
    const c = U.country(S.iso);
    if (!c || !c.regions) return;
    if (sel.options.length > 1 && sel.dataset.iso === S.iso) { sel.value = S.region; return; }
    sel.textContent = '';
    sel.add(new Option(tr('region_country', { country: c.name }), ''));
    let raw;
    try { raw = await U.regions(S.iso); } catch (e) { return; }
    const subj = raw.regions.filter(r => r.kind === 'subject').sort((a, b) => a.name.localeCompare(b.name, U.LOCALE));
    for (const r of subj) sel.add(new Option(r.name, r.code));
    sel.dataset.iso = S.iso;
    sel.value = S.region;
}

function currencySymbol(code) {
    if (!code) return '';
    try {
        const p = new Intl.NumberFormat(U.LOCALE, { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' }).formatToParts(0);
        const s = p.find(x => x.type === 'currency');
        return s ? s.value : code;
    } catch (e) { return code; }
}

async function ensureB() {
    if (dsB && dsB.meta && dsB.meta.iso === S.cmp.iso) return dsB;
    try { dsB = await U.dataset(S.cmp.iso); } catch (e) {
        dsB = null;
        U.diagnostic('dataset_failed', { iso: S.cmp.iso, msg: String(e && e.message || e).slice(0, 120) });
    }
    return dsB;
}

function showMode() {
    $$('[data-mode]').forEach(b => { const on = b.dataset.mode === S.mode; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    $$('[data-pane]').forEach(p => { p.hidden = p.dataset.pane !== S.mode; });
    $$('[data-own]').forEach(b => b.classList.toggle('on', b.dataset.own === S.own.kind));
    $$('[data-own-pane]').forEach(p => { p.hidden = p.dataset.ownPane !== S.own.kind; });
    $$('[data-fh]').forEach(b => b.classList.toggle('on', b.dataset.fh === S.forecast.how));
    $('#ic-fc-rate-wrap').hidden = S.forecast.how !== 'own';
}

function wire() {
    $$('[data-mode]').forEach(b => b.addEventListener('click', async () => {
        S.mode = b.dataset.mode;
        T.touch('mode');
        showMode();
        if (S.mode === 'compare') await ensureB();
        render();
        U.funnel('mode', { m: S.mode });
    }));
    $('#ic-rank-btn').addEventListener('click', () => { rankOpen = true; showRanking(); });
    $$('[data-own]').forEach(b => b.addEventListener('click', () => { S.own.kind = b.dataset.own; T.touch('own_kind'); showMode(); render(); }));
    $$('[data-fh]').forEach(b => b.addEventListener('click', () => { if (b.disabled) return; S.forecast.how = b.dataset.fh; T.touch('fc_how'); showMode(); render(); }));
    $$('[data-cv]').forEach(b => b.addEventListener('click', () => {
        S.convention = b.dataset.cv;
        $$('[data-cv]').forEach(x => x.classList.toggle('on', x === b));
        T.touch('convention');
        render();
        U.funnel('setting', { k: 'cv', v: S.convention });
    }));
    $$('[data-my]').forEach(box => box.addEventListener('change', () => {
        const [obj, key] = box.dataset.my.split('.');
        S[obj][key] = U.readMonth($('select[data-m]', box), $('select[data-y]', box));
        T.touch('period');
        render();
    }));
    const amt = (id, field, fn) => U.bindAmount($(id), v => { fn(v); T.touch(field); render(); });
    amt('#ic-amount', 'amount', v => { S.money.amount = v; });
    amt('#ic-a1', 'income_a1', v => { S.income.a1 = v; });
    amt('#ic-a2', 'income_a2', v => { S.income.a2 = v; });
    amt('#ic-own-amount', 'own_amount', v => { S.own.amount = v; });
    amt('#ic-fc-amount', 'fc_amount', v => { S.forecast.amount = v; });
    $('#ic-fc-years').addEventListener('input', e => { const v = Math.round(U.parseAmount(e.target.value)); S.forecast.years = v > 0 ? Math.min(v, 100) : NaN; T.touch('fc_years'); render(); });
    $('#ic-fc-rate').addEventListener('input', e => { S.forecast.rate = U.parseAmount(e.target.value); T.touch('fc_rate'); render(); });
    $$('[data-fy]').forEach(b => b.addEventListener('click', () => { S.forecast.years = +b.dataset.fy; $('#ic-fc-years').value = b.dataset.fy; T.touch('fc_years'); render(); }));
    $$('[data-w]').forEach(el => el.addEventListener('input', () => { const v = U.parseAmount(el.value); S.own.w[el.dataset.w] = Number.isFinite(v) && v >= 0 ? v : 0; T.touch('weights'); render(); }));
    $('#ic-w-reset').addEventListener('click', () => {
        const w = nationalDs.weights;
        if (!w) return;
        S.own.w = { food: w.food, nonfood: w.nonfood, services: w.services };
        $$('[data-w]').forEach(el => { el.value = U.num(S.own.w[el.dataset.w], 1); });
        T.touch('weights');
        render();
    });
    $('#ic-group').addEventListener('change', async e => {
        S.group = e.target.value;
        if (S.group !== 'all') S.region = '';
        T.touch('group');
        await applyRegion();
        render();
        U.funnel('setting', { k: 'group', v: S.group });
    });
    $('#ic-region').addEventListener('change', async e => { S.region = e.target.value; T.touch('region'); await applyRegion(); render(); U.funnel('region', { r: S.region ? 1 : 0 }); });
    $('#ic-official').addEventListener('change', e => { S.official = e.target.checked; T.touch('official'); render(); U.funnel('setting', { k: 'official', v: S.official ? 1 : 0 }); });
    $('#ic-presets').addEventListener('click', e => {
        const b = e.target.closest('[data-preset]');
        if (!b) return;
        const d = nationalDs, p = b.dataset.preset;
        const lastFullYear = C.km(d.k1) === 12 ? C.ky(d.k1) : C.ky(d.k1) - 1;
        const conv = S.convention || d.convention;
        if (p === 'year') { S.money.from = conv === 'period' ? C.mk(lastFullYear, 1) : C.mk(lastFullYear - 1, 12); S.money.to = C.mk(lastFullYear, 12); }
        if (p === '12m') { S.money.from = conv === 'period' ? d.k1 - 11 : d.k1 - 12; S.money.to = d.k1; }
        if (p === '2000') { S.money.from = Math.max(C.firstMonthIndex(d), C.mk(2000, 1)); S.money.to = d.k1; }
        // «Between months» can start ON the first published level; «inclusive»
        // needs the month before it.
        if (p === 'first') { S.money.from = conv === 'period' ? C.firstMonthIndex(d) : d.k0; S.money.to = d.k1; }
        const box = $('[data-my="money.from"]'), box2 = $('[data-my="money.to"]');
        U.writeMonth($('select[data-m]', box), $('select[data-y]', box), S.money.from);
        U.writeMonth($('select[data-m]', box2), $('select[data-y]', box2), S.money.to);
        T.touch('preset');
        render();
        U.funnel('preset', { p });
    });
    $('#ic-dep-rate').addEventListener('input', e => {
        const v = U.parseAmount(e.target.value);
        S.money.dep = Number.isFinite(v) && v > -100 ? v : NaN;
        T.touch('dep_rate');
        render();
    });
    $('#ic-dep').addEventListener('toggle', () => render());
    $('#ic-fxg').addEventListener('toggle', () => { S.fxg = $('#ic-fxg').open; if (S.fxg) T.touch('fxg'); render(); });
    if ($('#ic-prices')) $('#ic-prices').addEventListener('toggle', () => { S.prices = $('#ic-prices').open; if (S.prices) T.touch('prices'); render(); });
    $('#ic-rates').addEventListener('input', e => {
        const el = e.target.closest('[data-rate-year]');
        if (!el) return;
        const v = U.parseAmount(el.value);
        if (Number.isFinite(v)) S.own.rates[el.dataset.rateYear] = v; else delete S.own.rates[el.dataset.rateYear];
        T.touch('rates');
        render();
    });
    $('#ic-copy').addEventListener('click', () => { if (!lastText) return; U.copyText(lastText); T.action('copy', { m: S.mode }); });
    $('#ic-link').addEventListener('click', () => { U.shareLink(location.href, document.title); T.action('link', { m: S.mode }); });
    $('#ic-csv').addEventListener('click', () => {
        if (!lastCsv) return;
        const blob = new Blob(['\uFEFF' + lastCsv], { type: 'text/csv;charset=utf-8' });
        window.__downloadFile && window.__downloadFile(blob, 'inflation-' + S.iso.toLowerCase() + '.csv');
        T.action('csv', { m: S.mode });
    });
    $('#ic-pdf').addEventListener('click', async () => {
        if (!window.__generatePDF) return;
        const r = await window.__generatePDF({ element: $('#ic-report'), filename: 'inflation-' + S.iso.toLowerCase() + '.pdf', title: tr('pdf_title'), strings: U.T.pdf || undefined });
        if (r && r.ok) T.action('pdf', { m: S.mode });
    });
    // On a phone the answer sits below the whole form; the line under the
    // fields shows it as it changes and brings the full answer into view.
    $('#ic-live').addEventListener('click', () => {
        const a = $('#ic-report .tc-panel');
        if (a) a.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        U.funnel('live_jump', { m: S.mode });
    });
    T.drawers({ settings: $('.tc-rail .tc-drawer'), how: $('#ic-how') && $('#ic-how').closest('details'), years: $('#ic-years-wrap'), deposit: $('#ic-dep'), fxg: $('#ic-fxg'), prices: $('#ic-prices') });
    T.seen($('.ic-ref'), 'ref_tables');
    let rt = 0;
    window.addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(render, 150); });
}

// ── render ────────────────────────────────────────────────────────────────
function render() {
    if (!ds) return;
    const d = ds;
    const conv = S.convention || d.convention;
    $('#ic-status').textContent = statusLine(d);
    let r;
    try {
        r = ({ money: renderMoney, income: renderIncome, own: renderOwn, forecast: renderForecast, compare: renderCompare })[S.mode](d, conv);
    } catch (e) {
        window.__handleError && window.__handleError(e, 'inflation:render');
        return;
    }
    const state = {
        c: S.iso, m: S.mode, g: S.group !== 'all' ? S.group : '', cv: S.convention || '', of: S.official ? '' : '0', r: S.region,
        a: S.mode === 'money' ? S.money.amount : S.mode === 'own' ? S.own.amount : S.mode === 'forecast' ? S.forecast.amount : S.mode === 'compare' ? '' : S.income.a1,
        a2: S.mode === 'income' ? S.income.a2 : '',
        f: C.ymStr(S.mode === 'income' ? S.income.k1 : S.mode === 'own' ? S.own.from : S.mode === 'compare' ? S.cmp.from : S.money.from),
        t: C.ymStr(S.mode === 'income' ? S.income.k2 : S.mode === 'own' ? S.own.to : S.mode === 'compare' ? S.cmp.to : S.money.to),
        c2: S.mode === 'compare' ? S.cmp.iso : '',
        y: S.mode === 'forecast' ? S.forecast.years : '', fh: S.mode === 'forecast' ? S.forecast.how : '', fr: S.mode === 'forecast' && S.forecast.how === 'own' ? S.forecast.rate : '',
        dr: S.mode === 'money' && depositOn() ? S.money.dep : '',
        fg: S.mode === 'money' && S.fxg ? 1 : '',
        pr: S.mode === 'money' && S.prices && S.iso === 'RU' ? 1 : '',
    };
    U.writeHash(state);
    if (r && r.ok) T.result(JSON.stringify(state) + (S.mode === 'own' ? JSON.stringify([S.own.kind, S.own.w, S.own.rates]) : ''), Object.assign({ m: S.mode, iso: S.iso }, r.t || {}));
    else if (r && r.why !== 'loading') T.invalid(r.why, { m: S.mode });
    $('#ic-rank-wrap').hidden = S.mode !== 'compare' || !rankOpen;
    if ($('#ic-prices')) $('#ic-prices').hidden = S.iso !== 'RU';
    if (S.mode !== 'money' || S.iso !== 'RU' || !S.prices) $('#ic-prices-wrap').hidden = true;
    if (S.mode === 'compare' && rankOpen && r && r.ok) showRanking();
}

/** «в 1,85 раза», «в 171 449 раз» — two decimals only while they mean something. */
const multiple = x => U.num(x, x < 100 ? 2 : 0);

function statusLine(d) {
    const src = d.meta && d.meta.sourceName ? d.meta.sourceName : '';
    const checked = d.fetchedAt ? U.longDate(dateOf(d.fetchedAt)) : '';
    return tr('status', { source: src, last: U.monthYear(d.k1), checked });
}
const dateOf = iso => ({ y: +iso.slice(0, 4), m: +iso.slice(5, 7), d: +iso.slice(8, 10) });

function setAnswer(label, value, sub, note, tone) {
    $('#ic-answer-l').textContent = label;
    const v = $('#ic-answer-v');
    v.textContent = value;
    $('#ic-live-l').textContent = label;
    $('#ic-live-v').textContent = value;
    v.classList.toggle('muted', tone === 'muted');
    $('#ic-answer-sub').textContent = sub || '';
    $('#ic-answer-note').textContent = note || '';
    $('#ic-answer-note').hidden = !note;
    v.classList.remove('tc-flash'); void v.offsetWidth; v.classList.add('tc-flash');
}
function setStats(list) {
    $('#ic-stats').innerHTML = list.map(s => `<div class="tc-stat ${s.tone || ''}"><div class="tc-stat-l">${esc(s.l)}</div><div class="tc-stat-v">${esc(s.v)}</div>${s.s ? `<div class="tc-stat-s">${esc(s.s)}</div>` : ''}</div>`).join('');
}
function setMsgs(list) {
    $('#ic-msgs').innerHTML = list.filter(Boolean).map(m => `<p class="tc-msg ${m.tone || ''}">${esc(m.t)}</p>`).join('');
}
function fail(msgKey, vars) {
    setAnswer(tr('answer_none'), '—', tr(msgKey, vars), '', 'muted');
    setStats([]); setMsgs([]);
    $('#ic-how').innerHTML = '';
    $('#ic-years-wrap').hidden = true;
    $('#ic-chart-wrap').hidden = true;
    lastText = ''; lastCsv = '';
    return { ok: false, why: msgKey };
}

/**
 * A currency change in the words people use: «1000 old = 1 new» — or, when the
 * new money is the SMALLER unit (Australia 1966: 1 pound = 2 dollars), «1 old =
 * 2 new» rather than «0.5 old = 1 new».
 */
function reformVars(rf) {
    const inv = rf.ratio < 1;
    const r = inv ? 1 / rf.ratio : rf.ratio;
    return { inv, vars: { ratio: U.num(r, r % 1 ? 5 : 0), date: U.longDate(dateOf(rf.date)), old: rf.old, neu: rf.new } };
}

/** Australia before April 2024: one level per quarter — the period touches it. */
const quarterlyInside = (d, firstIndexMonth) => !!(d && d.source && d.source.quarterly_until && firstIndexMonth - 1 <= C.parseYm(d.source.quarterly_until));

/** Human lines for the parts of a chain: official years, months, forecast… */
function howLines(d, res, conv) {
    const lines = [];
    const months = [], years = [];
    for (const p of res.parts) {
        if (p.type === 'year') { years.push(p); continue; }
        if (years.length) { lines.push(yearLine(years.splice(0))); }
        if (p.type === 'months') lines.push(tr('how_months', { from: U.monthYear(p.from), to: U.monthYear(p.to), n: p.n, f: U.num(p.f, 4) }) + regionNote(d, p));
        else if (p.type === 'ytd') lines.push(tr('how_ytd', { from: U.monthYear(p.from), to: U.monthYear(p.to), v: U.num(p.f * 100, 2), prev: p.year - 1 }));
        else if (p.type === 'levels') lines.push(tr('how_levels', { from: U.monthYear(p.from - 1), to: U.monthYear(p.to), f: U.num(p.f, 4) }));
        else if (p.type === 'forecast') lines.push(tr('how_forecast', { from: U.monthYear(p.from), to: U.monthYear(p.to), rate: U.pct(p.rate, 1), f: U.num(p.f, 4) }));
        else if (p.type === 'custom') lines.push(tr('how_custom', { from: U.monthYear(p.from), to: U.monthYear(p.to), f: U.num(p.f, 4) }));
        months.push(p);
    }
    if (years.length) lines.push(yearLine(years));
    lines.unshift(conv === 'period'
        ? tr('how_conv_period', { from: U.monthYear(res.firstIndexMonth) })
        : tr('how_conv_point', { from: U.monthYear(res.firstIndexMonth - 1), to: U.monthYear(res.lastIndexMonth) }));
    // The by-hand number, when official prints were used and it differs visibly.
    if (d.kind === 'mm' && res.parts.some(p => p.type === 'year' || p.type === 'ytd')) {
        const hand = C.chainOnlyMonths(d, S.group, res.firstIndexMonth, Math.min(res.lastIndexMonth, d.k1));
        const official = C.chain(d, S.group, res.firstIndexMonth, Math.min(res.lastIndexMonth, d.k1), { official: true });
        if (hand && official && Math.abs(hand.f / official.f - 1) > 5e-6) {
            lines.push(tr('how_by_hand', { n: hand.months, f: U.num(hand.f, 4), diff: U.num(Math.abs(hand.f / official.f - 1) * 100, 3) }));
        }
    }
    for (const rf of res.reforms || []) { const x = reformVars(rf); lines.push(tr(x.inv ? 'how_reform_inv' : 'how_reform', x.vars)); }
    if (quarterlyInside(d, res.firstIndexMonth)) lines.push(tr('how_quarterly'));
    return lines;
}
function yearLine(ys) {
    const f = ys.reduce((a, p) => a * p.f, 1);
    const a = ys[0].year, b = ys[ys.length - 1].year;
    return tr(a === b ? 'how_year_one' : 'how_years', { from: a, to: b, f: U.num(f, 4) }) + (ds.meta && ds.meta.regionName ? ' ' + tr('how_region_years', { region: ds.meta.regionName }) : '');
}
function regionNote(d, p) {
    if (!d.meta || !d.meta.regionName) return '';
    return p.from < d.meta.regionFrom ? ' ' + tr('how_region_national') : ' ' + tr('how_region_months', { region: d.meta.regionName });
}
function setHow(lines, d) {
    const src = d.meta ? tr('source_' + (d.meta.source || 'x'), { checked: d.fetchedAt ? U.longDate(dateOf(d.fetchedAt)) : '', last: U.monthYear(d.k1) }) : '';
    $('#ic-how').innerHTML = lines.map(l => `<li>${esc(l)}</li>`).join('') + (src ? `<li class="ic-src">${esc(src)}</li>` : '');
}

/**
 * The price level over the period, start = 100. A level series (OECD) is the
 * ratio to the start month, so a missing month in the middle is stepped over;
 * multiplying month by month stopped the line at the first gap (the US has no
 * October 2025: BLS shutdown).
 */
function indexPoints(d, group, from, to, opts, conv) {
    const start = conv === 'period' ? from - 1 : from;
    const pts = [{ k: start, v: 100 }];
    const o = Object.assign({}, opts, { official: false });
    const ratio = d.kind === 'level' && !o.override;
    let f = 1;
    for (let k = start + 1; k <= to; k++) {
        const r = ratio ? C.chain(d, group, start + 1, k, o) : C.chain(d, group, k, k, o);
        if (!r) { if (ratio) continue; break; }
        f = ratio ? r.f : f * r.f;
        pts.push({ k, v: 100 * f });
    }
    return pts;
}

function chartIndex(d, from, to, opts, conv) {
    const pts = indexPoints(d, S.group, from, to, opts, conv);
    const wrap = $('#ic-chart-wrap');
    wrap.hidden = pts.length < 3;
    if (wrap.hidden) return;
    const max = Math.max(...pts.map(p => p.v));
    $('#ic-chart-cap').textContent = tr('chart_cap');
    U.drawLine($('#ic-chart'), pts, { log: max > 2000, label: v => U.num(v, v >= 1000 ? 0 : 1), forecastFrom: opts.forecast ? d.k1 : null });
}

function yearRows(d, from, to, amount, opts, conv) {
    const rows = [];
    let k = conv === 'period' ? from : from + 1;
    let run = amount;
    let cum = 1;
    while (k <= to) {
        const y = C.ky(k), end = Math.min(to, C.mk(y, 12));
        const r = C.chain(d, opts.group || S.group, k, end, opts);
        if (!r) break;
        cum *= r.f;
        const unit = C.unitShift(d.reforms, k - 1, end).f;
        run = run * r.f * unit;
        rows.push({ y, from: k, to: end, pct: (r.f - 1) * 100, cum: (cum - 1) * 100, sum: run, partial: C.km(k) !== 1 || C.km(end) !== 12, forecast: end > d.k1 });
        k = end + 1;
    }
    return rows;
}
function showYears(rows, cur, withSum) {
    const wrap = $('#ic-years-wrap');
    wrap.hidden = rows.length < 2;
    if (wrap.hidden) return;
    $('#ic-years').innerHTML = `<thead><tr><th>${esc(tr('col_year'))}</th><th class="num">${esc(tr('col_infl'))}</th><th class="num">${esc(tr('col_cum'))}</th>${withSum ? `<th class="num">${esc(tr('col_sum'))}</th>` : ''}</tr></thead><tbody>` +
        rows.map(r => `<tr><td>${r.y}${r.partial ? ` <span class="ic-part">${esc(U.monthYearShort(r.from).replace(/\s*\d{4}.*/, ''))}–${esc(U.monthYearShort(r.to).replace(/\s*\d{4}.*/, ''))}</span>` : ''}${r.forecast ? ' <span class="ic-part">' + esc(tr('forecast_mark')) + '</span>' : ''}</td><td class="num">${U.signedPct(r.pct, 2)}</td><td class="num">${U.signedPct(r.cum, 1)}</td>${withSum ? `<td class="num">${esc(U.money(r.sum, cur))}</td>` : ''}</tr>`).join('') + '</tbody>';
    lastCsv = [tr('col_year'), tr('col_infl'), tr('col_cum'), withSum ? tr('col_sum') : null].filter(Boolean).join(';') + '\n' +
        rows.map(r => [r.y, r.pct.toFixed(4), r.cum.toFixed(4), withSum ? r.sum.toFixed(2) : null].filter(x => x !== null).join(';')).join('\n');
}

// ── modes ─────────────────────────────────────────────────────────────────
function renderMoney(d, conv) {
    const { amount, from, to } = S.money;
    if (!(amount > 0)) return fail('err_amount');
    // Months after the last published one (the rest of the running year) are
    // filled by the central bank target — said out loud below, never silently.
    const fc = { rate: d.target ?? C.trailing12(d, 'all') };
    const opts = { convention: conv, official: S.official, forecast: fc };
    const res = C.money(d, S.group, amount, from, to, opts);
    if (!res) return fail('err_range', { first: U.monthYear(C.firstMonthIndex(d)), last: U.monthYear(d.k1) });
    const cur = d.currency;
    const back = res.backward;
    setAnswer(
        tr(back ? 'answer_back' : 'answer_fwd'),
        U.money(res.result, cur),
        tr('answer_sub', { amount: U.money(amount, cur), from: U.monthYear(from), to: U.monthYear(to) }),
        res.priceMultiple >= 1.005 || res.priceMultiple <= 0.995 ? tr(res.priceMultiple >= 1 ? 'note_prices_up' : 'note_prices_down', { x: multiple(res.priceMultiple >= 1 ? res.priceMultiple : 1 / res.priceMultiple) }) : tr('note_prices_flat'),
    );
    const stats = [
        { l: tr('st_total'), v: U.signedPct(res.totalPct, res.totalPct > 1000 ? 0 : 2) },
        { l: tr('st_annual'), v: U.pct(res.annualPct, 2), s: tr('st_months', { n: res.months }) },
        // 99.9994 % must not print as «100 %»: the money did not vanish entirely.
        { l: tr('st_lost'), v: U.signedPct(-res.lostPct, Math.abs(res.lostPct) > 99.95 ? 3 : 1), tone: res.lostPct > 0 ? 'bad' : 'good', s: tr('st_lost_s') },
    ];
    const msgs = [];
    const dep = depositOn() ? deposit(res, amount, back) : null;
    if (dep && dep.back) msgs.push({ t: tr('dep_back') });
    else if (dep) {
        stats.push({ l: tr('dep_stat', { rate: dep.rate }), v: U.money(dep.sum, cur), s: tr(dep.pa >= 0 ? 'dep_up' : 'dep_down', { pa: U.pct(Math.abs(dep.pa), 1) }), tone: dep.pa >= 0 ? 'good' : 'bad' });
        msgs.push({ t: dep.text, tone: dep.gain >= 0 ? 'good' : 'bad' });
    }
    const fxg = S.fxg ? dollarsAndGold(d, res, amount, from, to) : null;
    if (fxg) { stats.push(...fxg.stats); msgs.push(...fxg.msgs); }
    setStats(stats);
    if (res.reforms.length) { const x = reformVars(res.reforms[0]); msgs.push({ t: tr(x.inv ? 'msg_reform_inv' : 'msg_reform', x.vars), tone: 'warn' }); }
    if (res.parts.some(p => p.type === 'forecast')) msgs.push({ t: tr('msg_forecast_tail', { last: U.monthYear(d.k1), rate: U.pct(fc.rate, 1) }), tone: 'warn' });
    else if (Math.max(from, to) === d.k1 && d.kind === 'mm' && C.km(d.k1) !== 12) msgs.push({ t: tr('msg_running_year', { last: U.monthYear(d.k1) }) });
    setMsgs(msgs);
    setHow(howLines(d, res, conv), d);
    const lo = Math.min(from, to), hi = Math.max(from, to);
    chartIndex(d, lo, hi, { official: S.official, forecast: fc }, conv);
    showYears(yearRows(d, lo, hi, back ? res.result : amount, { official: S.official, forecast: fc }, conv), cur, true);
    if (fxg && fxg.stats.length) $('#ic-how').insertAdjacentHTML('beforeend', `<li class="ic-src">${esc(tr('how_fxg'))}</li>`);
    const pr = S.prices && S.iso === 'RU' && $('#ic-prices') ? renderPrices(from, to, res) : null;
    lastText = [
        tr('copy_head', { country: U.country(S.iso).name + (d.meta.regionName ? ', ' + d.meta.regionName : '') }),
        tr('answer_sub', { amount: U.money(amount, cur), from: U.monthYear(from), to: U.monthYear(to) }) + ' = ' + U.money(res.result, cur),
        tr('st_total') + ': ' + U.signedPct(res.totalPct, 2) + '; ' + tr('st_annual') + ': ' + U.pct(res.annualPct, 2),
        ...(dep && !dep.back ? [dep.text] : []),
        ...(fxg ? fxg.msgs.map(m => m.t) : []),
        ...(pr ? pr.lines : []),
        ...howLines(d, res, conv),
        location.href,
    ].join('\n');
    return { ok: true, t: { months: res.months, back: back ? 1 : 0, reform: res.reforms.length ? 1 : 0, region: S.region ? 1 : 0, fc: res.parts.some(p => p.type === 'forecast') ? 1 : 0, dep: dep && !dep.back ? Math.round(dep.pa * 10) / 10 : '' } };
}

const depositOn = () => Number.isFinite(S.money.dep) && $('#ic-dep').open;

/**
 * The same sum on a deposit at the person's rate, interest compounded monthly,
 * over the same months the price index covers — set against the sum that only
 * keeps up with prices. Tax on interest is left out and said so in the hint.
 */
function deposit(res, amount, back) {
    if (back) return { back: true };
    const r = S.money.dep, n = res.months, cur = ds.currency;
    const sum = amount * res.unit * Math.pow(1 + r / 1200, n);
    const gain = sum - res.result;
    const pa = (Math.pow(sum / res.result, 12 / n) - 1) * 100;
    const rate = U.pct(r, r % 1 ? 1 : 0);
    const text = tr(gain >= 0 ? 'dep_msg_up' : 'dep_msg_down', { rate, sum: U.money(sum, cur), gain: U.money(Math.abs(gain), cur), pa: U.pct(Math.abs(pa), 1) });
    return { sum, gain, pa, rate, text };
}

/**
 * «And had it been dollars, or gold?» The sum is first put into TODAY's units
 * with the country's reform table (BIS rates are in today's units throughout:
 * the rouble before 1998 is already new roubles, euro members are in euros),
 * changed at the month's average rate, held, and changed back — and set
 * against the sum that only keeps up with prices. Gold the same way through
 * the dollar. Forward only; the data are loaded the first time it is opened.
 */
function dollarsAndGold(d, res, amount, from, to) {
    const out = { stats: [], msgs: [] };
    if (res.backward) { out.msgs.push({ t: tr('fxg_back') }); return out; }
    const iso = S.iso, isUS = iso === 'US';
    const fxId = 'fx-' + iso.toLowerCase();
    if (!goldS || (!isUS && (!fxS || fxS.id !== fxId))) {
        if (fxgFailed.has(fxId) || fxgFailed.has('gold')) { out.msgs.push({ t: tr('fxg_failed'), tone: 'warn' }); return out; }
        out.msgs.push({ t: tr('fxg_loading') });
        if (fxgPending === fxId) return out;
        fxgPending = fxId;
        const load = (id, set) => U.series(id).then(set, e => {
            fxgFailed.add(id);
            U.diagnostic('series_failed', { id, msg: String(e && e.message || e).slice(0, 120) });
        });
        Promise.all([isUS ? null : load(fxId, s => { fxS = s; }), goldS ? null : load('gold', s => { goldS = s; })])
            .then(() => { fxgPending = null; if (S.mode === 'money') render(); });
        return out;
    }
    const cur = d.currency, base = res.result;
    const latest = d.k1;
    const toToday = k => C.unitShift(d.reforms, k, latest).f;          // × an amount in units at k → today's units
    // The month's value; a lone hole takes a neighbour, and a period ending a
    // month or two after the series (rates and gold publish later than some
    // prices: Iceland's September CPI, August rates) takes the last one — the
    // message names the month actually used.
    const pick = (s, k) => {
        if (k > s.k1 && k - s.k1 <= 2) k = s.k1;
        for (const j of [k, k - 1, k + 1]) { const v = U.at(s, j); if (v) return { v, k: j }; }
        return null;
    };
    const one = { v: 1 };
    const fFrom = isUS ? one : pick(fxS, from), fTo = isUS ? Object.assign({ k: to }, one) : pick(fxS, to);
    if (!fFrom || !fTo) {
        out.msgs.push({ t: tr('fx_none', { first: U.monthYear(fxS.k0), last: U.monthYear(fxS.k1) }) });
        return out;
    }
    const rFrom = fFrom.v, rTo = fTo.v;
    const usd = amount * toToday(from) / rFrom;
    const tile = (label, val) => {
        const diff = (val / base - 1) * 100;
        out.stats.push({ l: label, v: U.money(val, cur), s: tr(diff >= 0 ? 'fxg_more' : 'fxg_less', { pct: U.pct(Math.abs(diff), 1) }), tone: diff >= 0 ? 'good' : 'bad' });
    };
    if (!isUS) {
        const val = usd * rTo / toToday(to);
        tile(tr('fx_stat'), val);
        out.msgs.push({ t: tr('fx_msg', { amount: U.money(amount, cur), usd: U.money(usd, 'USD'), from: U.monthYear(from), sum: U.money(val, cur), to: U.monthYear(fTo.k) }) });
    }
    const gFrom = pick(goldS, from), gTo = pick(goldS, to);
    if (gFrom && gTo) {
        const oz = usd / gFrom.v;
        const val = oz * gTo.v * rTo / toToday(to);
        tile(tr('gold_stat'), val);
        out.msgs.push({ t: tr('gold_msg', { amount: U.money(amount, cur), oz: U.num(oz, oz >= 100 ? 0 : oz >= 1 ? 2 : 3), from: U.monthYear(from), sum: U.money(val, cur), to: U.monthYear(gTo.k) }) });
    } else {
        out.msgs.push({ t: tr('gold_none', { first: C.ky(goldS.k0) }) });
    }
    return out;
}

/**
 * «Цены тогда и сейчас»: Rosstat's average prices of everyday goods at the two
 * ends of the period. From 2000 the monthly price itself; before 2000 only
 * December prices exist, so a month in between is estimated by bending the
 * item's December-to-December change along its CPI group's path that year
 * (food, non-food, services — 80 % of 1998's food inflation came after
 * August) and marked «≈». Old prices are shown in the money of their date;
 * the growth is counted in today's money (the 1998 redenomination).
 */
function renderPrices(from, to, res) {
    const wrap = $('#ic-prices-wrap');
    wrap.hidden = false;
    const cap = $('#ic-prices-cap'), tbl = $('#ic-prices-tbl'), notes = $('#ic-prices-notes');
    if (!prices) {
        tbl.innerHTML = '';
        notes.innerHTML = '';
        cap.textContent = tr(pricesState === 'failed' ? 'pr_failed' : 'pr_loading');
        if (!pricesState) {
            pricesState = 'loading';
            fetch('/api/inflation/ru-prices.json', { credentials: 'omit' })
                .then(r => { if (!r.ok) throw new Error('prices http ' + r.status); return r.json(); })
                .then(j => { prices = Object.assign(j, { k0: C.parseYm(j.first), k1: C.parseYm(j.last) }); pricesState = ''; })
                .catch(e => { pricesState = 'failed'; U.diagnostic('prices_failed', { msg: String(e && e.message || e).slice(0, 120) }); })
                .then(() => { if (S.mode === 'money') render(); });
        }
        return null;
    }
    const nat = ds;                                   // reforms and CPI groups of the country
    const today = k => C.unitShift(nat.reforms, k, nat.k1).f;
    const at = (it, k) => {
        if (k > prices.k1) k = prices.k1;
        if (k >= prices.k0) { const v = it.series[k - prices.k0]; return v == null ? null : { v, k, est: false }; }
        const y = C.ky(k), m = C.km(k), dec = it.dec || {};
        if (m === 12 && dec[y] != null) return { v: dec[y], k, est: false };
        if (dec[y - 1] == null || dec[y] == null) return null;
        const k0 = C.mk(y - 1, 12), k1 = C.mk(y, 12);
        const a = dec[y - 1] * C.unitShift(nat.reforms, k0, k).f, b = dec[y] * C.unitShift(nat.reforms, k1, k).f;
        const g = nat.groups[it.group] ? it.group : 'all';
        let cumK = 1, cumY = 1;
        for (let j = k0 + 1; j <= k1; j++) {
            const x = (C.mmAt(nat, g, j) ?? 100) / 100;
            cumY *= x;
            if (j <= k) cumK *= x;
        }
        const w = Math.abs(Math.log(cumY)) > 1e-9 ? Math.log(cumK) / Math.log(cumY) : (k - k0) / 12;
        return { v: a * Math.pow(b / a, w), k, est: true };
    };
    const rows = prices.items.map(it => {
        const a = at(it, from), b = at(it, to);
        const x = a && b ? (b.v * today(b.k)) / (a.v * today(a.k)) : null;
        return { it, a, b, x };
    });
    const kTo = Math.min(to, prices.k1);
    cap.textContent = tr('pr_cap', { from: U.monthYear(from), to: U.monthYear(kTo) });
    const cell = p => (p ? (p.est ? '≈ ' : '') + U.money(p.v, 'RUB', p.v < 1000) : '—');
    tbl.innerHTML = `<thead><tr><th>${esc(tr('pr_col_item'))}</th><th class="num">${esc(U.monthYear(from))}</th><th class="num">${esc(U.monthYear(kTo))}</th><th class="num">${esc(tr('pr_col_x'))}</th></tr></thead><tbody>` +
        rows.map(r => `<tr><td>${esc(r.it.name)}${r.it.unit ? `<small>${esc(r.it.unit)}</small>` : ''}</td><td class="num">${esc(cell(r.a))}</td><td class="num">${esc(cell(r.b))}${r.x ? `<small class="ic-pr-xs">${esc(U.ltr('×' + multiple(r.x)))}</small>` : ''}</td><td class="num ic-pr-x">${r.x ? esc(U.ltr('×' + multiple(r.x))) : '—'}</td></tr>`).join('') + '</tbody>';

    const ok = rows.filter(r => r.x).sort((p, q) => q.x - p.x);
    const n = [];
    const all = res.backward ? 1 / res.priceMultiple : res.priceMultiple;
    if (ok.length > 2) {
        const fast = ok[0], slow = ok[ok.length - 1];
        n.push(tr('pr_msg', { all: multiple(all), fast: fast.it.name.toLowerCase(), fx: multiple(fast.x), slow: slow.it.name.toLowerCase(), sx: multiple(slow.x) }));
    }
    if (rows.some(r => (r.a && r.a.est) || (r.b && r.b.est))) n.push(tr('pr_est'));
    if (Math.min(from, to) < C.mk(1998, 1)) n.push(tr('pr_old'));
    if (to > prices.k1) n.push(tr('pr_last', { last: U.monthYear(prices.k1) }));
    const lo = Math.min(from, to), hi = Math.max(from, to);
    for (const it of prices.items) {
        if (it.note && it.codes.some(([f]) => { const k = C.parseYm(f); return k > lo && k <= hi; })) n.push(it.name + ': ' + it.note.charAt(0).toLowerCase() + it.note.slice(1));
    }
    n.push(tr('pr_src'));
    notes.innerHTML = n.map(t => `<p class="tc-hint">${esc(t)}</p>`).join('');
    return { lines: [tr('pr_cap', { from: U.monthYear(from), to: U.monthYear(kTo) }) + ':', ...rows.filter(r => r.a && r.b).map(r => `${r.it.name}${r.it.unit ? ', ' + r.it.unit : ''}: ${cell(r.a)} → ${cell(r.b)} (×${multiple(r.x)})`)] };
}

function renderIncome(d, conv) {
    const { a1, k1, a2, k2 } = S.income;
    if (!(a1 > 0) || !(a2 > 0)) return fail('err_amount');
    if (k2 <= k1) return fail('err_order');
    const res = C.factor(d, S.group, k1, k2, { convention: 'point', official: S.official });
    if (!res) return fail('err_range', { first: U.monthYear(C.firstMonthIndex(d)), last: U.monthYear(d.k1) });
    const ch = C.realChange(a1, a2, res.priceMultiple, res.unit);
    const cur = d.currency;
    const good = ch.realPct >= 0;
    setAnswer(tr(good ? 'inc_up' : 'inc_down'), U.signedPct(ch.realPct, 1),
        tr('inc_sub', { a1: U.money(a1, cur), k1: U.monthYear(k1), a2: U.money(a2, cur), k2: U.monthYear(k2) }),
        tr('inc_needed', { sum: U.money(ch.needed, cur) }));
    setStats([
        { l: tr('inc_nominal'), v: U.signedPct(ch.nominalPct, 1) },
        { l: tr('inc_prices'), v: U.signedPct((res.priceMultiple - 1) * 100, 1) },
        { l: tr('inc_gap'), v: U.signedMoney(ch.gap, cur), tone: good ? 'good' : 'bad', s: tr(good ? 'inc_gap_s_up' : 'inc_gap_s_down') },
    ]);
    setMsgs([res.reforms.length ? (x => ({ t: tr(x.inv ? 'msg_reform_inv' : 'msg_reform', x.vars), tone: 'warn' }))(reformVars(res.reforms[0])) : null,
        { t: tr('inc_point_note') }]);
    setHow(howLines(d, res, 'point'), d);
    chartIndex(d, k1, k2, { official: S.official }, 'point');
    $('#ic-years-wrap').hidden = true;
    lastCsv = '';
    lastText = [tr('copy_head', { country: U.country(S.iso).name }), $('#ic-answer-sub').textContent, tr(good ? 'inc_up' : 'inc_down') + ': ' + U.signedPct(ch.realPct, 1), tr('inc_needed', { sum: U.money(ch.needed, cur) }), location.href].join('\n');
    return { ok: true, t: { real: Math.round(ch.realPct), months: res.months } };
}

function renderOwn(d, conv) {
    const { from, to, amount } = S.own;
    if (to < from) return fail('err_order');
    const cur = d.currency;
    if (S.own.kind === 'basket') {
        const w = S.own.w, total = (w.food || 0) + (w.nonfood || 0) + (w.services || 0);
        $('#ic-w-sum').textContent = tr('w_sum', { sum: U.num(total, 1) });
        if (!(total > 0)) return fail('err_weights');
        const b = C.basket(nationalDs, w, from, to, { convention: conv, official: S.official });
        const off = C.factor(nationalDs, 'all', from, to, { convention: conv, official: S.official });
        if (!b || !off) return fail('err_range', { first: U.monthYear(C.firstMonthIndex(d)), last: U.monthYear(d.k1) });
        const mine = (b.priceMultiple - 1) * 100, official = (off.priceMultiple - 1) * 100;
        setAnswer(tr('own_answer'), U.signedPct(mine, 2), tr('own_sub', { from: U.monthYear(from), to: U.monthYear(to) }),
            tr(mine > official ? 'own_more' : 'own_less', { official: U.signedPct(official, 2), diff: U.num(Math.abs(mine - official), 2) }));
        // The weight fields' short names («Продукты»), not the index titles:
        // «Продовольственные товары» ran out of a phone-width tile.
        setStats(b.used.map(u => ({ l: tr('w_' + u.g).replace(/\s*[,،]\s*%\s*$/, '') + ' · ' + U.num(u.w / total * 100, 1) + ' %', v: U.signedPct((u.f - 1) * 100, 1) })));
        setMsgs([{ t: tr('own_method') }]);
        setHow([tr('own_how', { n: b.used.length })].concat(howLines(nationalDs, off, conv).slice(0, 1)), nationalDs);
        $('#ic-chart-wrap').hidden = true;
        $('#ic-years-wrap').hidden = true;
        lastCsv = 'group;weight;inflation_pct\n' + b.used.map(u => [u.g, u.w, ((u.f - 1) * 100).toFixed(4)].join(';')).join('\n');
        lastText = [tr('copy_head', { country: U.country(S.iso).name }), tr('own_sub', { from: U.monthYear(from), to: U.monthYear(to) }), tr('own_answer') + ': ' + U.signedPct(mine, 2), tr('own_more', { official: U.signedPct(official, 2), diff: U.num(Math.abs(mine - official), 2) }).split('.')[0], location.href].join('\n');
        return { ok: true, t: { own: 'basket' } };
    }
    // Own yearly rates: the table lists the years of the period; an empty cell
    // keeps the official figure for that year.
    renderRatesTable(d, from, to);
    if (!(amount > 0)) return fail('err_amount');
    const rates = S.own.rates;
    const override = k => {
        const r = rates[C.ky(k)];
        return Number.isFinite(r) ? Math.pow(1 + r / 100, 1 / 12) * 100 : null;
    };
    const any = Object.keys(rates).some(y => +y >= C.ky(from) && +y <= C.ky(to));
    const fc = { rate: d.target ?? C.trailing12(d, 'all') };
    const res = C.money(d, S.group, amount, from, to, { convention: conv, official: S.official, override, forecast: fc });
    const off = C.money(d, S.group, amount, from, to, { convention: conv, official: S.official, forecast: fc });
    if (!res || !off) return fail('err_range', { first: U.monthYear(C.firstMonthIndex(d)), last: U.monthYear(d.k1) });
    setAnswer(tr('rates_answer'), U.money(res.result, cur), tr('answer_sub', { amount: U.money(amount, cur), from: U.monthYear(from), to: U.monthYear(to) }),
        any ? tr('rates_vs', { official: U.money(off.result, cur) }) : tr('rates_hint'));
    setStats([
        { l: tr('st_total'), v: U.signedPct(res.totalPct, 2) },
        { l: tr('st_annual'), v: U.pct(res.annualPct, 2), s: tr('st_months', { n: res.months }) },
    ]);
    setMsgs([]);
    setHow(howLines(d, res, conv), d);
    chartIndex(d, Math.min(from, to), Math.max(from, to), { official: S.official, override, forecast: fc }, conv);
    showYears(yearRows(d, from, to, amount, { official: S.official, override, forecast: fc }, conv), cur, true);
    lastText = [tr('copy_head', { country: U.country(S.iso).name }), $('#ic-answer-sub').textContent + ' = ' + U.money(res.result, cur), ...howLines(d, res, conv), location.href].join('\n');
    return { ok: true, t: { own: 'rates', custom: any ? 1 : 0 } };
}

function renderRatesTable(d, from, to) {
    const box = $('#ic-rates');
    const y0 = C.ky(from), y1 = C.ky(to);
    const key = y0 + '-' + y1 + '-' + S.iso;
    if (box.dataset.key === key) return;
    box.dataset.key = key;
    const yt = new Map(C.yearTable(d, 'all').map(r => [r.year, r]));
    let html = '';
    for (let y = y0; y <= y1; y++) {
        const off = yt.get(y);
        const ph = off && off.dec != null ? U.num(off.dec - 100, 2) : (d.target != null ? U.num(d.target, 1) : '');
        const val = Number.isFinite(S.own.rates[y]) ? U.num(S.own.rates[y], 2) : '';
        html += `<label class="tc-f ic-rate"><span class="tc-f-l">${y}${off && off.partial ? ' · ' + esc(tr('partial_mark', { n: off.partial })) : ''}${!off ? ' · ' + esc(tr('forecast_mark')) : ''}</span><input class="tc-in" data-rate-year="${y}" inputmode="decimal" autocomplete="off" placeholder="${esc(ph)}" value="${esc(val)}"></label>`;
    }
    box.innerHTML = html;
}

function renderForecast(d) {
    const { amount, years, how } = S.forecast;
    if (!(amount > 0)) return fail('err_amount');
    if (!(years > 0)) return fail('err_years');
    const rate = how === 'target' ? d.target : how === 'trailing' ? C.trailing12(d, 'all') : S.forecast.rate;
    if (!Number.isFinite(rate) || rate <= -100) return fail('err_rate');
    const f = Math.pow(1 + rate / 100, years);
    const cur = d.currency;
    setAnswer(tr('fc_answer', { n: years }), U.money(amount * f, cur), tr('fc_sub', { amount: U.money(amount, cur), n: years, rate: U.pct(rate, 1) }),
        tr('fc_power', { sum: U.money(amount / f, cur), n: years }));
    const t12 = C.trailing12(d, 'all');
    setStats([
        { l: tr('fc_growth'), v: U.signedPct((f - 1) * 100, 1) },
        { l: tr('fc_rate_used'), v: U.pct(rate, 1), s: tr('fc_how_' + how) },
        { l: tr('fc_now'), v: U.pct(t12, 1), s: tr('fc_now_s', { last: U.monthYear(d.k1) }) },
    ]);
    setMsgs([{ t: tr('fc_disclaimer'), tone: 'warn' }]);
    setHow([tr('fc_how', { rate: U.pct(rate, 1), n: years })], d);
    const pts = [];
    for (let i = 0; i <= years * 12; i++) pts.push({ k: d.k1 + i, v: 100 * Math.pow(1 + rate / 100, i / 12) });
    $('#ic-chart-wrap').hidden = false;
    $('#ic-chart-cap').textContent = tr('chart_cap');
    U.drawLine($('#ic-chart'), pts, { label: v => U.num(v, 0), forecastFrom: d.k1 });
    const rows = [];
    for (let i = 1; i <= Math.min(years, 50); i++) rows.push({ y: C.ky(d.k1) + i, from: d.k1, to: d.k1, pct: rate, cum: (Math.pow(1 + rate / 100, i) - 1) * 100, sum: amount * Math.pow(1 + rate / 100, i), partial: false, forecast: false });
    showYears(rows, cur, true);
    lastText = [tr('copy_head', { country: U.country(S.iso).name }), tr('fc_sub', { amount: U.money(amount, cur), n: years, rate: U.pct(rate, 1) }) + ': ' + U.money(amount * f, cur), tr('fc_disclaimer'), location.href].join('\n');
    return { ok: true, t: { how, years } };
}

// ── two countries side by side ─────────────────────────────────────────────
/**
 * Each country's price growth in its OWN currency over the same months, both
 * counted ONE way — the person's convention, or the first country's. Two
 * screenshots side by side compared Russia «inclusive» with the UK «between
 * months»: one month apart. Not exchange rates, not living standards — the
 * hint under the fields says so. A Russian region compares as the region.
 */
function renderCompare(dA, conv) {
    const cA = U.country(S.iso), cB = U.country(S.cmp.iso);
    if (!cB || S.cmp.iso === S.iso) return fail('cmp_same');
    if (!dsB || !dsB.meta || dsB.meta.iso !== S.cmp.iso) {
        ensureB().then(d => { if (d && S.mode === 'compare') render(); });
        return { ok: false, why: 'loading' };
    }
    const dB = dsB;
    const { from, to } = S.cmp;
    if (!(to > from)) return fail('err_order');
    // 'all' for both: the other country may have no food/services index
    const opts = { convention: conv, official: S.official, group: 'all' };
    const rA = C.factor(dA, 'all', from, to, opts);
    if (!rA) return fail('err_range', { first: U.monthYear(C.firstMonthIndex(dA)), last: U.monthYear(dA.k1) });
    const rB = C.factor(dB, 'all', from, to, opts);
    if (!rB) return fail('cmp_no_data', { country: cB.name, first: U.monthYear(C.firstMonthIndex(dB)), last: U.monthYear(dB.k1) });

    const nameA = cA.name + (dA.meta && dA.meta.regionName ? ', ' + dA.meta.regionName : '');
    const pct = r => (r.priceMultiple - 1) * 100;
    const perYear = r => (Math.pow(r.priceMultiple, 12 / r.months) - 1) * 100;
    setAnswer(tr('cmp_answer'), U.ltr('×' + multiple(rA.priceMultiple) + ' · ×' + multiple(rB.priceMultiple)),
        tr('cmp_sub', { a: nameA, b: cB.name, from: U.monthYear(from), to: U.monthYear(to) }), tr('cmp_note'));
    setStats([
        { l: nameA, v: U.signedPct(pct(rA), pct(rA) >= 1000 ? 0 : 2), s: tr('cmp_avg', { pct: U.pct(perYear(rA), 2) }) },
        { l: cB.name, v: U.signedPct(pct(rB), pct(rB) >= 1000 ? 0 : 2), s: tr('cmp_avg', { pct: U.pct(perYear(rB), 2) }) },
    ]);
    setMsgs([]);
    const src = d => (d.meta ? tr('source_' + (d.meta.source || 'x'), { checked: d.fetchedAt ? U.longDate(dateOf(d.fetchedAt)) : '', last: U.monthYear(d.k1) }) : '');
    const q = [dA, dB].some(d => quarterlyInside(d, rA.firstIndexMonth)) ? `<li>${esc(tr('how_quarterly'))}</li>` : '';
    $('#ic-how').innerHTML = `<li>${esc(tr('cmp_conv_' + conv))}</li>` + q + [dA, dB].map(d => `<li class="ic-src">${esc(src(d))}</li>`).join('');

    // chart: both lines from 100; a log scale when one outgrew the other many times
    const pA = indexPoints(dA, 'all', from, to, opts, conv), pB = indexPoints(dB, 'all', from, to, opts, conv);
    const ends = [pA[pA.length - 1].v, pB[pB.length - 1].v];
    const log = Math.max(...ends) / Math.max(Math.min(...ends), 1) >= 8 || Math.max(...ends) > 2000;
    const wrap = $('#ic-chart-wrap');
    wrap.hidden = pA.length < 3 || pB.length < 3;
    if (!wrap.hidden) {
        $('#ic-chart-cap').textContent = tr(log ? 'chart_cap_log' : 'chart_cap');
        U.drawLines($('#ic-chart'), [{ points: pA, name: nameA }, { points: pB, name: cB.name }], { log, label: v => U.num(v, v >= 1000 ? 0 : 1) });
    }

    // by year, side by side
    const yA = yearRows(dA, from, to, 1, opts, conv), yB = new Map(yearRows(dB, from, to, 1, opts, conv).map(r => [r.y, r]));
    const wrapY = $('#ic-years-wrap');
    wrapY.hidden = yA.length < 2;
    if (!wrapY.hidden) {
        $('#ic-years').innerHTML = `<thead><tr><th>${esc(tr('col_year'))}</th><th class="num">${esc(nameA)}</th><th class="num">${esc(cB.name)}</th></tr></thead><tbody>` +
            yA.map(r => { const b = yB.get(r.y); return `<tr><td>${r.y}${r.partial ? ` <span class="ic-part">${esc(U.monthYearShort(r.from).replace(/\s*\d{4}.*/, ''))}–${esc(U.monthYearShort(r.to).replace(/\s*\d{4}.*/, ''))}</span>` : ''}</td><td class="num">${U.signedPct(r.pct, 2)}</td><td class="num">${b ? U.signedPct(b.pct, 2) : '—'}</td></tr>`; }).join('') + '</tbody>';
    }
    lastCsv = [tr('col_year'), nameA, cB.name].join(';') + '\n' + yA.map(r => { const b = yB.get(r.y); return [r.y, r.pct.toFixed(4), b ? b.pct.toFixed(4) : ''].join(';'); }).join('\n');
    const line = (n, r) => `${n}: ×${multiple(r.priceMultiple)} (${U.signedPct(pct(r), 2)}; ${tr('cmp_avg', { pct: U.pct(perYear(r), 2) })})`;
    lastText = [tr('copy_head_cmp', { a: nameA, b: cB.name }), $('#ic-answer-sub').textContent, line(nameA, rA), line(cB.name, rB), tr('cmp_conv_' + conv), location.href].join('\n');
    return { ok: true, t: { b: cB.iso, months: rA.months, log: log ? 1 : 0 } };
}

/**
 * Every country over the same months, by how far prices rose — the neutral
 * answer to «and how is it elsewhere?». Loaded on request: one small dataset
 * per country, cached for the page.
 */
let rankSeq = 0;
async function showRanking() {
    const wrap = $('#ic-rank-wrap');
    wrap.hidden = false;
    const seq = ++rankSeq;
    const { from, to } = S.cmp;
    const conv = S.convention || nationalDs.convention;
    const opts = { convention: conv, official: S.official, group: 'all' };
    if (!$('#ic-rank').innerHTML) $('#ic-rank-cap').textContent = tr('rank_loading');
    const rows = await Promise.all(U.countries().map(async c => {
        try { return { c, r: C.factor(await U.dataset(c.iso), 'all', from, to, opts) }; } catch (e) { return { c, r: null }; }
    }));
    if (seq !== rankSeq) return;
    rows.sort((a, b) => (b.r ? b.r.priceMultiple : -1) - (a.r ? a.r.priceMultiple : -1));
    $('#ic-rank-cap').textContent = tr('rank_title', { from: U.monthYear(from), to: U.monthYear(to) });
    let n = 0;
    $('#ic-rank').innerHTML = `<thead><tr><th>#</th><th>${esc(tr('col_country'))}</th><th class="num">${esc(tr('col_growth'))}</th><th class="num">${esc(tr('col_avg'))}</th></tr></thead><tbody>` +
        rows.map(({ c, r }) => {
            const cls = c.iso === S.iso ? 'ic-rank-a' : c.iso === S.cmp.iso ? 'ic-rank-b' : r ? '' : 'ic-rank-none';
            const name = `<img class="ic-flag" src="${esc(c.flag)}" alt="" width="18" height="13" loading="lazy">${esc(c.name)}`;
            if (!r) return `<tr class="${cls}"><td>—</td><td>${name}</td><td class="num" colspan="2">${esc(tr('rank_missing'))}</td></tr>`;
            // «×26,13», not «+2 513 %»: over decades the percentage runs to ten
            // digits (Turkey 1966–2026: «+3 499 460 971 %»), the multiple stays readable.
            const py = (Math.pow(r.priceMultiple, 12 / r.months) - 1) * 100;
            return `<tr class="${cls}"><td>${++n}</td><td>${name}</td><td class="num">${U.ltr('×' + multiple(r.priceMultiple))}</td><td class="num">${U.pct(py, 2)}</td></tr>`;
        }).join('') + '</tbody>';
    if (seq === 1) U.funnel('rank_open', { n, a: S.iso, b: S.cmp.iso });
}

// ── reference tables under the tool (server prints the default country) ───
function renderReference() {
    const d = nationalDs;
    const c = U.country(S.iso);
    $$('[data-ref-country]').forEach(el => { el.textContent = c.name; });
    const yt = C.yearTable(d, 'all').reverse();
    $('#ic-ref-years').innerHTML = yt.map(r => `<tr><td>${r.year}${r.partial ? ' · ' + esc(tr('partial_mark', { n: r.partial })) : ''}</td><td class="num">${r.dec != null ? U.signedPct(r.dec - 100, 2) : '—'}</td><td class="num">${r.avg != null ? U.signedPct(r.avg - 100, 2) : '—'}</td></tr>`).join('');
    const mt = C.monthTable(d, 'all', 12);
    $('#ic-ref-months').innerHTML = mt.map(r => `<tr><td>${esc(U.monthYear(r.k))}</td><td class="num">${r.mm != null ? U.num(r.mm, 2) : '—'}</td><td class="num">${r.ytd != null ? U.num(r.ytd, 2) : '—'}</td><td class="num">${r.yoy != null ? U.num(r.yoy, 2) : '—'}</td></tr>`).join('');
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
